import React from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { View, Text, Image } from '../../lib/react-native-compat';
import { selectedJobAtom, selectedJobDataAtom, jobUserDataAtom } from '../../store/company';
import JobSwitcher from './JobSwitcher';
import bannerImg from '../../assets/strikefield.jpg';
import strikeLogo from '../../assets/logo.png';

// Hero header for the report list: jobsite photo behind a dark gradient, the
// job number/description (tap to switch jobs), and the high-level schedule —
// start → completion with an elapsed bar and days remaining.
//
// Dates match what Fusion shows on Job Info: the forecast dates saved in
// Forecast_User_Data (startDateActual / completionDateForecast /
// mechanicallyComplete) win, and the JDE job record is only the fallback when a
// job has never had them set. Reading the rows directly rather than the shared
// atoms avoids showing the previous job's dates on a job with none of its own.

// Dates are stored naive (no zone) — parse as UTC so they don't slide a day.
const fmt = (d) => (d ? moment.utc(d).format('MMM D, YYYY') : null);
const day = (d) => moment.utc(moment.utc(d).format('YYYY-MM-DD'));

const Meta = ({ label, value }) => (
    <View style={{ marginRight: 20, marginTop: 6 }}>
        <Text style={{ fontSize: 10, fontWeight: '700', color: 'rgba(255,255,255,0.55)', letterSpacing: 0.6 }}>
            {label.toUpperCase()}
        </Text>
        <Text style={{ fontSize: 13, fontWeight: '600', color: '#fff' }}>{value}</Text>
    </View>
);

const JobHeader = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [jobData] = useAtom(selectedJobDataAtom);
    const [jobUserData] = useAtom(jobUserDataAtom);

    const forecastDate = (field) => (jobUserData || [])
        .find((r) => r?.Forecast_Field_Name === field)?.Forecast_Value || null;

    const start = forecastDate('startDateActual') || jobData?.Actual_Start_Date || jobData?.Planned_Start_Date;
    const finish = forecastDate('completionDateForecast') || jobData?.Planned_Complete_Date;
    const mech = forecastDate('mechanicallyComplete') || jobData?.Mechanical_Complete_Date;
    const pm = jobData?.Project_Manager;
    const workType = jobData?.Work_Type_Description;

    // Schedule progress — only when both ends are known and ordered.
    let pct = null;
    let daysLeft = null;
    if (start && finish) {
        const total = day(finish).diff(day(start), 'days');
        if (total > 0) {
            const today = moment.utc(moment().format('YYYY-MM-DD'));
            const gone = today.diff(day(start), 'days');
            pct = Math.max(0, Math.min(100, Math.round((gone / total) * 100)));
            daysLeft = day(finish).diff(today, 'days');
        }
    }
    const overdue = daysLeft !== null && daysLeft < 0;

    return (
        <View style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', marginBottom: 16 }}>
            <View style={{
                position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                backgroundImage: `url(${bannerImg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center 45%',
            }}
            />
            <View style={{
                position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                background: 'linear-gradient(100deg, rgba(15,23,42,0.94) 0%, rgba(15,23,42,0.80) 55%, rgba(29,78,216,0.62) 100%)',
            }}
            />

            <View style={{ position: 'relative', paddingTop: 14, paddingBottom: 14, paddingLeft: 16, paddingRight: 16 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        {/* Full-color logo washed to white so it reads on the dark banner. */}
                        <Image
                            source={strikeLogo}
                            alt="Strike"
                            style={{ height: 14, filter: 'brightness(0) invert(1)', opacity: 0.9 }}
                        />
                        <View style={{ width: 1, height: 12, backgroundColor: 'rgba(255,255,255,0.35)', marginLeft: 10, marginRight: 10 }} />
                        <Text style={{ fontSize: 10, fontWeight: '700', color: 'rgba(255,255,255,0.6)', letterSpacing: 1 }}>
                            DAILY REPORTS
                        </Text>
                    </View>
                    {workType ? (
                        <View style={{
                            borderRadius: 999, border: '1px solid rgba(255,255,255,0.3)',
                            paddingTop: 2, paddingBottom: 2, paddingLeft: 10, paddingRight: 10,
                        }}
                        >
                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff' }}>{workType}</Text>
                        </View>
                    ) : null}
                </View>

                {/* Tapping the title opens the job picker. */}
                <View style={{ marginTop: 4, marginBottom: 2 }}>
                    <JobSwitcher
                        variant="line"
                        textStyle={{ fontSize: 18, fontWeight: '700', color: '#fff', textAlign: 'left' }}
                    />
                </View>

                <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {fmt(start) && <Meta label="Start" value={fmt(start)} />}
                    {fmt(mech) && <Meta label="Mech. Complete" value={fmt(mech)} />}
                    {fmt(finish) && <Meta label="Complete" value={fmt(finish)} />}
                    {pm ? <Meta label="Project Manager" value={pm} /> : null}
                    {!fmt(start) && !fmt(finish) && selectedJob ? (
                        <Meta label="Schedule" value="No planned dates set" />
                    ) : null}
                </View>

                {/* Past the completion date the bar goes red — a full blue bar
                    reads as "done, all good", which is the opposite of an
                    overrun. */}
                {pct !== null && (
                    <View style={{ marginTop: 12 }}>
                        <View style={{ height: 6, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.22)', overflow: 'hidden' }}>
                            <View style={{ width: `${pct}%`, height: 6, borderRadius: 999, backgroundColor: overdue ? '#F87171' : '#38BDF8' }} />
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                            <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>{`${pct}% of schedule elapsed`}</Text>
                            <Text style={{ fontSize: 11, fontWeight: '700', color: overdue ? '#FCA5A5' : '#fff' }}>
                                {overdue ? `${Math.abs(daysLeft)} days past completion` : `${daysLeft} days remaining`}
                            </Text>
                        </View>
                    </View>
                )}
            </View>
        </View>
    );
};

export default JobHeader;
